import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiService } from "../../services/apiService"; // Import the reusable API service
import { decodeToken, setCookieToken } from "../../utils";
import { LoginResponse } from "../../types";

// Define the shape of the state
interface AuthState {
  user: object | null;
  accessToken: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

// Initial state
const initialState: AuthState = {
  user: null,
  accessToken: null,
  loading: false,
  error: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setUserAndToken(state, action) {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false; // Ensure logout resets authentication state
    },
    loginState: (state) => {
      state.isAuthenticated = true;
    },
    logoutState: (state) => {
      state.isAuthenticated = false;
    },
  },
});

export const { setLoading, setUserAndToken, setError, logout, loginState, logoutState } = authSlice.actions;

// Thunk for login
export const loginUser = createAsyncThunk<LoginResponse, { username: string; password: string }>(
  "auth/loginUser",
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.login(credentials); // Using apiService.login
      // Dispatch loginState to update isAuthenticated to true
      dispatch(loginState()); // Mark user as authenticated
      setCookieToken("isAuthenticated", true); // Optionally set a cookie if needed
      setCookieToken("token", response.data.accessToken); // Optionally set a cookie if needed
      const decodedToken = decodeToken(response.data.accessToken);
      setCookieToken("user", JSON.stringify(decodedToken)); // Optionally set a cookie if needed
      return response.data;  // Return the login response (user data, token)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Login failed");
    }
  }
);


export default authSlice.reducer;
